/**
 * Hook useDealPipelines - CRUD de Pipelines CRM
 * 
 * Gerencia criação, edição e exclusão de pipelines de deals
 * Segue arquitetura de segurança tenant v2.0
 * 
 * Versão: 1.0.0
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('DealPipelines');

/**
 * Hook para gerenciar pipelines CRM
 */
export function useDealPipelines() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Mutation para criar pipeline
  // Recebe workspaceId diretamente - não busca automaticamente
  const createPipeline = useMutation({
    mutationFn: async ({ name, color, workspaceId }: { name: string; color: string; workspaceId: string }) => {
      // Validação de entrada robusta
      if (!currentTenant?.id) {
        throw new Error('Tenant não encontrado');
      }
      
      if (!workspaceId) {
        throw new Error('Workspace ativo não encontrado. Recarregue a página.');
      }

      logger.debug('Criando pipeline:', { tenantId: currentTenant.id, workspaceId, name });

      // Busca maior sort_order atual DENTRO DO WORKSPACE (não global)
      const { data: maxOrder, error: maxError } = await supabase
        .from('crm_pipelines')
        .select('sort_order')
        .eq('tenant_id', currentTenant.id)
        .eq('workspace_id', workspaceId)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle(); // maybeSingle para não falhar quando não há pipelines

      if (maxError) {
        logger.error('Erro ao buscar sort_order:', maxError.message);
        throw new Error('Erro ao preparar criação do pipeline');
      }

      const nextOrder = (maxOrder?.sort_order ?? -1) + 1;

      // Cria pipeline no workspace especificado (v1.1.0: persiste a cor escolhida)
      const { data: pipeline, error } = await supabase
        .from('crm_pipelines')
        .insert({
          tenant_id: currentTenant.id,
          workspace_id: workspaceId,
          name,
          color: color || null, // NULL = fallback automático no frontend
          sort_order: nextOrder,
          is_active: true,
          is_default: false,
        })
        .select()
        .single();

      if (error) {
        logger.error('Erro ao criar pipeline:', error.message);
        throw error;
      }

      // Cria fases padrão para o novo pipeline
      const defaultPhases = [
        { name: 'Novo Lead', color: '#3b82f6', sort_order: 0, probability_percent: 10 },
        { name: 'Qualificação', color: '#f59e0b', sort_order: 1, probability_percent: 30 },
        { name: 'Proposta', color: '#8b5cf6', sort_order: 2, probability_percent: 60 },
        { name: 'Negociação', color: '#06b6d4', sort_order: 3, probability_percent: 80 },
        { name: 'Ganho', color: '#10b981', sort_order: 4, probability_percent: 100, is_won: true },
        { name: 'Perdido', color: '#ef4444', sort_order: 5, probability_percent: 0, is_lost: true },
      ];

      const { error: phasesError } = await supabase
        .from('crm_pipeline_phases')
        .insert(
          defaultPhases.map(phase => ({
            ...phase,
            pipeline_id: pipeline.id,
            tenant_id: currentTenant.id,
          }))
        );

      if (phasesError) {
        logger.warn('Erro ao criar fases padrão:', phasesError.message);
        // Não falha a operação - pipeline foi criado
      }

      return pipeline;
    },
    onSuccess: (_data, variables) => {
      // Invalida cache do workspace específico e hierarquia
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines-by-workspace', currentTenant?.id, variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-hierarchy', currentTenant?.id] });
      // Invalida o bootstrap do Kanban (RPC `get_crm_kanban_bootstrap`)
      queryClient.invalidateQueries({ queryKey: ['crm-kanban-bootstrap'] });
      toast.success('Pipeline criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar pipeline: ${error.message}`);
    },
  });

  // Mutation para atualizar pipeline (v1.1.0: persiste nome E cor)
  const updatePipeline = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { name?: string; color?: string } }) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      // Atualiza nome e cor (cor vazia/undefined => NULL = fallback automático)
      const dbUpdates: { name?: string; color?: string | null } = {};
      if (updates.name) dbUpdates.name = updates.name;
      if (updates.color !== undefined) dbUpdates.color = updates.color || null;

      const { data, error } = await supabase
        .from('crm_pipelines')
        .update(dbUpdates)
        .eq('id', id)
        .eq('tenant_id', currentTenant.id)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao atualizar pipeline:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['crm-kanban-bootstrap'] });
      toast.success('Pipeline atualizado!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar pipeline: ${error.message}`);
    },
  });

  // Mutation para excluir pipeline (soft delete)
  const deletePipeline = useMutation({
    mutationFn: async (id: string) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      // Verifica se é o pipeline padrão
      const { data: pipeline } = await supabase
        .from('crm_pipelines')
        .select('is_default')
        .eq('id', id)
        .single();

      if (pipeline?.is_default) {
        throw new Error('Não é possível excluir o pipeline padrão');
      }

      // Soft delete (is_active = false)
      const { error } = await supabase
        .from('crm_pipelines')
        .update({ is_active: false })
        .eq('id', id)
        .eq('tenant_id', currentTenant.id);

      if (error) {
        logger.error('Erro ao excluir pipeline:', error.message);
        throw error;
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['crm-kanban-bootstrap'] });
      toast.success('Pipeline excluído!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir pipeline: ${error.message}`);
    },
  });

  // Mutation para reordenar pipelines de um workspace (v1.1.0)
  // Persiste a nova ordem (sort_order) via RPC segura `reorder_crm_pipelines`.
  const reorderPipelines = useMutation({
    mutationFn: async ({ workspaceId, pipelineIds }: { workspaceId: string; pipelineIds: string[] }) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');
      if (!workspaceId) throw new Error('Workspace não encontrado');

      const { error } = await supabase.rpc('reorder_crm_pipelines', {
        p_workspace_id: workspaceId,
        p_pipeline_ids: pipelineIds,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao reordenar pipelines:', error.message);
        throw error;
      }

      return true;
    },
    onSuccess: () => {
      // Invalida todas as fontes que exibem pipelines ordenados
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines-by-workspace'] });
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['crm-kanban-bootstrap'] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao reordenar pipelines: ${error.message}`);
    },
  });

  return {
    createPipeline,
    updatePipeline,
    deletePipeline,
    reorderPipelines,
  };

}
